<template>
  <div class="search-ai card">
    <!-- Form -->
    <search-ai-form v-model="prompt" @receive-data="handleReceiveData" @reset="handleReset" />

    <!-- Unknown -->
    <el-alert v-if="status === 'unknown'"
      title="Can't create search based on the prompt, please modify the prompt and try again" type="error" show-icon
      class="mt-3" />

    <!-- Loading -->
    <div v-else-if="loading" class="mt-3">
      <el-skeleton :rows="4" animated />
    </div>

    <!-- Idle / empty -->
    <el-empty v-else-if="status === 'idle' && !hasAnyResults" description="Enter a prompt and click Search"
      class="mt-3" />

    <!-- Results -->
    <template v-else-if="entityType === 'Teams'">
      <!-- Teams -->
      <team-preview-list v-if="entityType === 'Teams'" :teams="teamArray" :total="total" :with-card="true"
        :show-dividers="true" />
    </template>

    <!-- Events -->
    <template v-else-if="entityType === 'Events'">
      <event-preview-list v-if="entityType === 'Events'" :events="eventArray" :total="total" :with-card="true"
        :show-dividers="true" />
    </template>

    <!-- Users -->
    <template v-else-if="entityType === 'Users'">
      <user-preview-list v-if="entityType === 'Users'" :users="userArray" :total="total" :with-card="true"
        :show-dividers="true" />
    </template>
  </div>
</template>

<script>
import SearchAIForm from '@/components/ai/SearchAIForm.vue';
import TeamPreviewList from '@/components/teams/TeamPreviewList.vue';
import EventPreviewList from '@/components/events/EventPreviewList.vue';
import UserPreviewList from '@/components/people/UserPreviewList.vue';

export default {
  name: 'SearchAI',
  components: {
    SearchAIForm,
    'search-ai-form': SearchAIForm, // safe alias
    TeamPreviewList,
    'team-preview-list': TeamPreviewList, // safe alias
    EventPreviewList,
    'event-preview-list': EventPreviewList, // safe alias
    UserPreviewList,
    'user-preview-list': UserPreviewList, // safe alias
  },
  data() {
    return {
      prompt: '',
      loading: false,
      status: 'idle', // 'idle' | 'ok' | 'unknown'
      entityType: null, // 'Teams' | 'Events' | 'Users' | 'Unknown' | null

      teamArray: [],
      eventArray: [],
      userArray: [],
      total: null,
    };
  },
  computed: {
    hasAnyResults() {
      if (this.entityType === 'Teams') return this.teamArray.length > 0;
      return Array.isArray(this.items) && this.items.length > 0;
    },
  },
  methods: {
    /**
     * Receives data from <search-ai-form> which already called searchAIPrompt.
     * Expected payload: { entity_type: 'Teams'|'Events'|'Users'|'Unknown', items: any[], total?: number }
     */
    handleReceiveData(payload) {
      try {

        console.log('Received AI data:', payload);


        this.loading = false;
        this.status = 'idle';
        this.entityType = null;
        // clear previous results
        this.teamArray = [];
        this.eventArray = [];
        this.userArray = [];
        this.total = null;

        const data = payload || {};
        const items = Array.isArray(data.items) ? data.items : [];
        const total = typeof data.total === 'number' ? data.total : items.length;

        if (!items) {
          this.status = 'unknown';
          this.entityType = 'Unknown';
          this.total = 0;
          return;
        }

        if (items[0].teamId) {
          console.log('Items appear to be Teams:', items);
          this.entityType = 'Teams';
        }
        else if (items[0].eventId) {
          console.log('Items appear to be Events:', items);
          this.entityType = 'Events';
        }
        else if (items[0].userId) {
          console.log('Items appear to be Users:', items);
          this.entityType = 'Users';
        }
        else {
          console.log('Items type is unknown:', items);
          this.entityType = 'Unknown';
          this.status = 'unknown';
          return;
        }

        this.status = 'ok';

        if (this.entityType === 'Teams') {
          this.teamArray = items;
          this.total = total;
        } else if (this.entityType === 'Events') {
          this.eventArray = items;
          this.total = total;
        } else if (this.entityType === 'Users') {
          this.userArray = items;
          this.total = total;
        } else {
          console.warn('Unhandled entity type:', this.entityType);
          this.status = 'unknown';
          this.total = 0;
          return;
        }
      } catch (e) {
        console.error('Failed to process AI results:', e);
        this.status = 'unknown';
        this.entityType = 'Unknown';
        this.teamArray = [];
        this.items = [];
        this.total = 0;
      }
    },
    
    handleReset() {
      this.prompt = '';
      this.loading = false;
      this.status = 'idle';
      this.entityType = null;
      this.teamArray = [];
      this.userArray = [];
      this.eventArray = [];
      this.total = null;
    },

    formatDate(val) {
      try {
        if (!val) return '';
        const d = new Date(val);
        if (isNaN(d.getTime())) return String(val);
        return d.toLocaleString();
      } catch {
        return String(val);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.search-ai {
  max-width: 1200px;
}

.mt-3 {
  margin-top: 1rem;
}

.section-title {
  margin: 0 0 0.5rem 0;
  font-weight: 600;
}

.mb-3 {
  margin-bottom: 1rem;
}

.result-card {
  min-height: 120px;
}

.card-title {
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 4px;
}

.card-sub {
  color: var(--el-text-color-secondary);
  font-size: 13px;
  margin-bottom: 6px;
}

.card-desc {
  color: var(--el-text-color-primary);
  font-size: 13px;
  line-height: 1.4;
}


</style>
